/**
 * Fee schedule: standard rate keys, labels, and default rates ($/hr).
 * Used by retainer and request-funds modals; can be overridden by expert profile when backend provides rates.
 */

export const STANDARD_RATE_KEYS = [
  'consultationRate',
  'expeditedReviewRate',
  'reportWritingRate',
  'depositionRate',
  'trialRate',
] as const;

export type StandardRateKey = (typeof STANDARD_RATE_KEYS)[number];

export interface FeeScheduleData extends Record<StandardRateKey, number> {}

/** Options for "Hourly fee for this task" dropdown; order matches display. */
export const HOURLY_FEE_OPTIONS: readonly {
  key: StandardRateKey;
  label: string;
}[] = [
  { key: 'consultationRate', label: 'Consultation' },
  { key: 'expeditedReviewRate', label: 'Expedited review' },
  { key: 'reportWritingRate', label: 'Report writing' },
  { key: 'depositionRate', label: 'Deposition' },
  { key: 'trialRate', label: 'Trial testimony' },
];

/**
 * TODO(backend): Replace with expert-specific rates from profile or API.
 */
export function defaultFeeSchedule(baseHourlyRate: number): FeeScheduleData {
  return {
    consultationRate: baseHourlyRate,
    expeditedReviewRate: Math.round(baseHourlyRate * 1.2),
    reportWritingRate: Math.round(baseHourlyRate * 1.25),
    depositionRate: Math.round(baseHourlyRate * 1.5),
    trialRate: Math.round(baseHourlyRate * 2),
  };
}

export interface MeExpertRatesInput {
  hourlyRate?: number | null;
  recordsReviewHourlyRate?: number | null;
  examinationHourlyRate?: number | null;
  depositionHourlyRate?: number | null;
  testimonyHourlyRate?: number | null;
}

/**
 * Maps expert `me` rates ($/hr) to retainer modal keys.
 * DB: records review → expedited review; examination → report writing;
 * deposition → deposition; testimony → trial; hourly → consultation.
 * Any missing slot falls back to `defaultFeeSchedule` from base hourly.
 */
export function feeScheduleFromMeExpertRates(
  rates: MeExpertRatesInput,
): FeeScheduleData {
  const baseHourly = rates.hourlyRate ?? 0;
  const defaults = defaultFeeSchedule(baseHourly);

  /** Prefer the expert's stored rate; otherwise the slot's default from `defaultFeeSchedule(baseHourly)`. */
  function rateOrDefault(
    storedRate: number | null | undefined,
    derivedDefault: number,
  ): number {
    if (storedRate === null || storedRate === undefined) {
      return derivedDefault;
    }
    if (Number.isNaN(storedRate)) {
      return derivedDefault;
    }
    return storedRate;
  }

  return {
    consultationRate: rateOrDefault(
      rates.hourlyRate,
      defaults.consultationRate,
    ),
    expeditedReviewRate: rateOrDefault(
      rates.recordsReviewHourlyRate,
      defaults.expeditedReviewRate,
    ),
    reportWritingRate: rateOrDefault(
      rates.examinationHourlyRate,
      defaults.reportWritingRate,
    ),
    depositionRate: rateOrDefault(
      rates.depositionHourlyRate,
      defaults.depositionRate,
    ),
    trialRate: rateOrDefault(rates.testimonyHourlyRate, defaults.trialRate),
  };
}

export function getRetainerFeeTypeLabel(req: {
  billingType?: 'hourly' | 'flat_rate';
  hours: number;
}): string {
  return req.billingType === 'flat_rate' || req.hours === 0
    ? 'Flat fee'
    : 'Hourly';
}
